import { getCookie } from '@/configs/sdk';
import type { IUtmData } from '@/interface/utm-data';

import { stripLocalePrefix } from './post-login-redirect';
import { sanitizeUTM } from './sanitize-utm';
import { isTrackingExemptRoute } from './tracking-exempt-route';

const UTM_STORAGE_KEY = 'noping_utm_data';

/**
 * Saves UTM data to sessionStorage
 */
const saveUtmToStorage = (data: Partial<IUtmData>) => {
  if (typeof window === 'undefined') return;

  try {
    sessionStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(data));
  } catch (error) {
    // Silently fail if sessionStorage is not available
  }
};

/**
 * Retrieves UTM data from sessionStorage
 */
const getUtmFromStorage = (): Partial<IUtmData> => {
  if (typeof window === 'undefined') return {};

  try {
    const stored = sessionStorage.getItem(UTM_STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    return {};
  }
};

/**
 * Extracts UTM parameters from the current URL
 */
const extractUtmFromUrl = (): Partial<IUtmData> => {
  if (typeof window === 'undefined') return {};

  const urlParams = new URLSearchParams(window.location.search);

  const rawUtmData: IUtmData = {
    utm_source: sanitizeUTM(urlParams.get('utm_source')),
    utm_medium: sanitizeUTM(urlParams.get('utm_medium')),
    utm_campaign: sanitizeUTM(urlParams.get('utm_campaign')),
    utm_content: sanitizeUTM(urlParams.get('utm_content')),
    utm_term: sanitizeUTM(urlParams.get('utm_term')),
    ad_id: sanitizeUTM(urlParams.get('ad_id')),
    device: sanitizeUTM(urlParams.get('device')),
    matchtype: sanitizeUTM(urlParams.get('matchtype')),
    network: sanitizeUTM(urlParams.get('network')),
    adposition: sanitizeUTM(urlParams.get('adposition')),
    placement: sanitizeUTM(urlParams.get('placement')),
    gclid: sanitizeUTM(urlParams.get('gclid')),
  };

  return Object.fromEntries(Object.entries(rawUtmData).filter(([_, value]) => value !== null));
};

/**
 * Attaches the click id to the OUTPUT, instead of mixing it into the URL reading above.
 *
 * It used to be read inside `extractUtmFromUrl`, and that made a click id alone look like "the URL
 * carries tracking data" to `getUtmData` below — which then overwrote the whole stored blob with just
 * `{ clickId }`, wiping the `utm_source`/`utm_campaign`/`gclid` of the landing that actually brought
 * the visitor. It stayed invisible while the reader depended on the SDK: `getCookie()` answered `''`
 * until `Affiliate.init()` ran, so the branch fell through to storage. Now that
 * `src/utils/click-id.ts` answers synchronously from the cookie, the URL or the session copy, it
 * answers on EVERY page load — `useUtmTracker` calls this from `_app` — and the wipe became the
 * common case for any visitor of a partner campaign.
 *
 * The two have different lifetimes and different owners, which is why they are no longer persisted
 * together: the UTM blob belongs to the landing, while the click id has its own storage
 * (`src/utils/click-id-store.ts`, whose comment describes this same overwrite trap) and is re-read on
 * every call, which is strictly fresher than a copy inside the blob. Any stale key left in storage by
 * the previous behaviour is dropped here, so `getClickId()` stays the single source of truth.
 */
const withClickId = ({ clickId: _staleStoredClickId, ...data }: Partial<IUtmData>): Partial<IUtmData> => {
  const clickId = getCookie();

  // A blank id has to be an absent key, never an empty string — see src/utils/click-id.ts.
  return clickId ? { ...data, clickId } : data;
};

/**
 * Returns UTM data, prioritizing URL parameters and saving to sessionStorage
 * If no parameters in URL, uses data saved in sessionStorage
 */
export const getUtmData = (): Partial<IUtmData> => {
  if (typeof window === 'undefined') {
    return {};
  }

  // The SSO handoff pages (/auth/*, SCRUM-1955) never capture (SCRUM-2438). `_app` already turns
  // `useUtmTracker` off there, but `AuthContext` reaches this reader on those pages too, so the check
  // lives here. `window.location.pathname` keeps the locale prefix `router.pathname` drops, hence the strip.
  // The stored landing is still returned: it is what the SSO body has to carry.
  if (isTrackingExemptRoute(stripLocalePrefix(window.location.pathname))) {
    return withClickId(getUtmFromStorage());
  }

  // Try to get from URL first
  const urlUtmData = extractUtmFromUrl();

  // If found data in URL, save to storage and return. The overwrite is deliberate for UTM — a new
  // landing replaces the previous one rather than merging two campaigns into a hybrid — and it is
  // exactly why the click id must not take part in this decision.
  if (Object.keys(urlUtmData).length > 0) {
    saveUtmToStorage(urlUtmData);
    return withClickId(urlUtmData);
  }

  // If no data in URL, return saved data from storage
  return withClickId(getUtmFromStorage());
};

/**
 * The UTMs for an SSO login body — browser (`AuthContext`) and mobile relay (`postMobileSso`) alike.
 * Without them the first SSO login creates the account with an empty `user_registration.add_info` and
 * no `user_tracking_data` row (SCRUM-2390). The camelCase `clickId` is dropped: those bodies carry the
 * partner id as `click_id`, from `clickIdBody()`.
 */
const withoutClickId = ({ clickId: _clickId, ...utm }: Partial<IUtmData>): Partial<IUtmData> => utm;

export const getSsoUtmData = (): Partial<IUtmData> => withoutClickId(getUtmData());
